import * as React from "react";
import { useRecordContext, ReferenceField, useCreatePath } from "react-admin";
import { Link } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import { labelOf, colorOf } from "../enums";
import {
  fmtDate,
  fmtDateTime,
  fmtMoney,
  fmtCents,
  fullName,
  initialsOf,
  isTechnicalKey,
} from "../utils/format";

const get = (row, path) => {
  if (typeof path === "function") return path(row);
  if (!path) return undefined;
  return String(path)
    .split(".")
    .reduce((v, p) => (v == null ? undefined : v[p]), row);
};

// ── En-tête de page ─────────────────────────────────────────────────────────
export const PageHeader = ({ title, subtitle, icon, actions, sx }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    alignItems={{ xs: "flex-start", sm: "center" }}
    justifyContent="space-between"
    spacing={1.5}
    sx={{ mb: 2.5, ...sx }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
      {icon && (
        <Box
          sx={(t) => ({
            width: 40,
            height: 40,
            borderRadius: 3,
            display: "grid",
            placeItems: "center",
            bgcolor: alpha(t.palette.primary.main, 0.1),
            color: "primary.main",
            flexShrink: 0,
          })}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h4" component="h1" sx={{ lineHeight: 1.2 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
    {actions && (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {actions}
      </Stack>
    )}
  </Stack>
);

// ── Carte de section ─────────────────────────────────────────────────────────
export const Section = ({ title, icon, action, children, sx, contentSx, dense }) => (
  <Card sx={{ height: "100%", ...sx }}>
    <CardContent sx={{ p: dense ? 2 : 2.5, ...contentSx }}>
      {(title || action) && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.75 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            {icon && <Box sx={{ color: "primary.main", display: "flex", "& svg": { fontSize: 20 } }}>{icon}</Box>}
            <Typography variant="subtitle1" sx={{ fontSize: 15 }}>{title}</Typography>
          </Stack>
          {action}
        </Stack>
      )}
      {children}
    </CardContent>
  </Card>
);

// Grille responsive simple (CSS grid) — évite les différences d'API Grid MUI v5/v7.
export const CardGrid = ({ min = 280, gap = 2, children, sx }) => (
  <Box
    sx={{
      display: "grid",
      gap,
      gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${min}px), 1fr))`,
      alignItems: "stretch",
      ...sx,
    }}
  >
    {children}
  </Box>
);

// ── Pastille de statut (enum → libellé FR + couleur) ─────────────────────────
export const StatusChip = ({ value, enumObj, size = "small", sx }) => {
  if (value == null || value === "") return <Typography variant="body2" color="text.disabled" component="span">—</Typography>;
  const color = enumObj ? colorOf(enumObj, value) : "default";
  return (
    <Chip
      size={size}
      color={color}
      label={enumObj ? labelOf(enumObj, value) : String(value)}
      icon={
        <Box
          component="span"
          sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor", ml: "8px !important", mr: "-2px !important" }}
        />
      }
      sx={{ "& .MuiChip-icon": { color: "inherit" }, ...sx }}
    />
  );
};

// Version champ react-admin (dans un Datagrid).
export const StatusField = ({ source, enumObj }) => {
  const r = useRecordContext();
  return <StatusChip value={get(r, source)} enumObj={enumObj} />;
};

// ── Champs date / montant pour Datagrid ─────────────────────────────────────
export const DateTimeField = ({ source, withTime = true }) => {
  const r = useRecordContext();
  const v = get(r, source);
  return (
    <Typography variant="body2" component="span" sx={{ whiteSpace: "nowrap" }}>
      {withTime ? fmtDateTime(v) : fmtDate(v)}
    </Typography>
  );
};

export const MoneyField = ({ source, cents = false }) => {
  const r = useRecordContext();
  const v = get(r, source);
  return (
    <Typography variant="body2" component="span" sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", fontWeight: 500 }}>
      {cents ? fmtCents(v) : fmtMoney(v)}
    </Typography>
  );
};

// ── Personne (avatar + nom + lien vers la fiche) ─────────────────────────────
export const PersonChip = ({ id, firstName, lastName, name, email, photo, resource = "patients", dense, stop = true }) => {
  const label = name || fullName(firstName, lastName) || email || "Sans nom";
  const content = (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Avatar
        src={photo || undefined}
        sx={(t) => ({
          width: dense ? 24 : 30,
          height: dense ? 24 : 30,
          fontSize: dense ? 11 : 12,
          fontWeight: 700,
          bgcolor: alpha(resource === "professionals" ? t.palette.primary.main : t.palette.info.main, 0.14),
          color: resource === "professionals" ? "primary.dark" : "info.dark",
        })}
      >
        {initialsOf(firstName || label.split(" ")[0], lastName || label.split(" ")[1])}
      </Avatar>
      <Box sx={{ minWidth: 0, lineHeight: 1.2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {label}
        </Typography>
        {email && !dense && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {email}
          </Typography>
        )}
      </Box>
    </Stack>
  );
  if (!id) return content;
  return (
    <Box
      component={Link}
      to={`/${resource}/${encodeURIComponent(id)}/show`}
      onClick={stop ? (e) => e.stopPropagation() : undefined}
      sx={{ textDecoration: "none", color: "inherit", display: "inline-flex", maxWidth: "100%", "&:hover .MuiTypography-body2": { color: "primary.main" } }}
    >
      {content}
    </Box>
  );
};

export const isProUser = (u) => u?.type === "PROFESSIONAL" || u?.type === "PRO" || u?.userType === "PROFESSIONAL";

const UserNameInner = ({ dense }) => {
  const u = useRecordContext();
  if (!u) return null;
  if (!u.firstName && !u.lastName && !u.email) {
    return <Typography variant="body2" color="text.secondary">Utilisateur introuvable</Typography>;
  }
  return (
    <PersonChip
      id={u.id}
      firstName={u.firstName}
      lastName={u.lastName}
      email={dense ? undefined : u.email}
      photo={u?.photo?.url || u?.urlPhoto}
      resource={isProUser(u) ? "professionals" : "patients"}
      dense={dense}
    />
  );
};

// Remplace une colonne « xxxId » par le nom de l'utilisateur (GET /users/{id}).
// Seuls les identifiants « UUID » sont des ids de compte (GET /users/{id}) ;
// une autre valeur (UID Firebase, nom…) n'est pas résolue : pas de 404.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUserId = (v) => UUID_RE.test(String(v || ""));

export const UserRefField = ({ source, dense = true, emptyText = "—" }) => {
  const r = useRecordContext();
  const v = get(r, source);
  if (v == null || v === "") return <Typography variant="body2" color="text.disabled" component="span">{emptyText}</Typography>;
  if (!isUserId(v)) {
    return <Typography variant="body2" color="text.secondary" component="span">Compte non rattaché</Typography>;
  }
  return (
    <ReferenceField source={source} reference="users" link={false} emptyText={emptyText}>
      <UserNameInner dense={dense} />
    </ReferenceField>
  );
};

// ── Bouton copier ────────────────────────────────────────────────────────────
export const CopyButton = ({ value, size = "small" }) => {
  const [done, setDone] = React.useState(false);
  if (value == null || value === "") return null;
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(String(value));
    } catch (_) {
      const ta = document.createElement("textarea");
      ta.value = String(value);
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch (__) {}
      document.body.removeChild(ta);
    }
    setDone(true);
    setTimeout(() => setDone(false), 1200);
  };
  return (
    <Tooltip title={done ? "Copié" : "Copier"}>
      <IconButton size={size} onClick={copy} aria-label="Copier" sx={{ p: 0.5 }}>
        {done ? <CheckIcon sx={{ fontSize: 15 }} color="success" /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
      </IconButton>
    </Tooltip>
  );
};

// Collecte récursive (profondeur limitée) des identifiants techniques d'un
// enregistrement : [{ key: "paymentInformation.id", value }].
export const collectTechnical = (record, prefix = "", depth = 0, out = []) => {
  if (!record || typeof record !== "object" || depth > 2) return out;
  Object.entries(record).forEach(([k, v]) => {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v == null || v === "") return;
    if (Array.isArray(v)) {
      if (isTechnicalKey(k) && v.every((x) => typeof x !== "object")) out.push({ key, value: v.join(", ") });
      return;
    }
    if (typeof v === "object") {
      collectTechnical(v, key, depth + 1, out);
      return;
    }
    if (isTechnicalKey(k)) out.push({ key, value: v });
  });
  return out;
};

const KEY_LABELS = {
  id: "Identifiant",
  uid: "UID Firebase",
  patientId: "Patient",
  professionalId: "Professionnel",
  proId: "Professionnel",
  userId: "Utilisateur",
  bookingId: "Rendez-vous",
  idSpeciality: "Spécialité",
  specialityId: "Spécialité",
  forfaitId: "Forfait",
  procheId: "Proche",
  paymentIntent: "Stripe PaymentIntent",
  customerId: "Stripe Customer",
  stripeAccountId: "Compte Stripe",
  transferId: "Virement Stripe",
  payoutId: "Payout Stripe",
};
const labelForKey = (key) => {
  const last = key.split(".").pop();
  const base = KEY_LABELS[last] || last;
  return key.includes(".") ? `${base} (${key.split(".").slice(0, -1).join(".")})` : base;
};

// ── Informations techniques (repliées) ──────────────────────────────────────
// Liste tous les identifiants avec bouton copier + la donnée brute complète.
export const TechInfo = ({ items, showRaw = true, title = "Informations techniques", defaultExpanded = false, sx }) => {
  const record = useRecordContext();
  const list = items || collectTechnical(record);
  const [raw, setRaw] = React.useState(false);
  if (!record && !items) return null;
  return (
    <Accordion defaultExpanded={defaultExpanded} sx={{ mt: 2, bgcolor: "transparent", ...sx }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: 1 } }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <FingerprintIcon fontSize="small" sx={{ color: "text.secondary" }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
            {title}
          </Typography>
          <Chip size="small" label={`${list.length} identifiant${list.length > 1 ? "s" : ""}`} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {list.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Aucun identifiant.</Typography>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, columnGap: 3, rowGap: 0.5 }}>
            {list.map(({ key, value, label }) => (
              <Stack key={key} direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, py: 0.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 150, flexShrink: 0, fontWeight: 600 }}>
                  {label || labelForKey(key)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, wordBreak: "break-all", minWidth: 0 }}
                >
                  {String(value)}
                </Typography>
                <CopyButton value={value} />
              </Stack>
            ))}
          </Box>
        )}
        {showRaw && record && (
          <Box sx={{ mt: 1.5 }}>
            <Typography
              component="button"
              type="button"
              onClick={() => setRaw((x) => !x)}
              variant="caption"
              sx={{ border: 0, bgcolor: "transparent", color: "primary.main", cursor: "pointer", fontWeight: 600, p: 0 }}
            >
              {raw ? "Masquer la donnée brute (JSON)" : "Afficher la donnée brute (JSON)"}
            </Typography>
            {raw && (
              <Box
                component="pre"
                sx={(t) => ({
                  mt: 1,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#F5F7F5",
                  fontSize: 12,
                  maxHeight: 360,
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  m: 0,
                })}
              >
                {JSON.stringify(record, null, 2)}
              </Box>
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

// ── Paires label / valeur compactes ─────────────────────────────────────────
export const KV = ({ label, children, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, display: "block" }}>
      {label}
    </Typography>
    <Box sx={{ mt: 0.25, fontSize: 14, wordBreak: "break-word" }}>
      {children != null ? children : value == null || value === "" ? (
        <Typography variant="body2" color="text.disabled">—</Typography>
      ) : (
        <Typography variant="body2">{value}</Typography>
      )}
    </Box>
  </Box>
);

export const KVGrid = ({ children, min = 180 }) => (
  <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))` }}>
    {children}
  </Box>
);

// Lien « Ouvrir → » vers une fiche, sans propagation (dans un Datagrid).
export const ShowLink = ({ resource, id, children = "Ouvrir" }) => {
  const createPath = useCreatePath();
  if (!id) return null;
  return (
    <Box
      component={Link}
      to={createPath({ resource, id, type: "show" })}
      onClick={(e) => e.stopPropagation()}
      sx={{ color: "primary.main", fontWeight: 600, textDecoration: "none", fontSize: 13, whiteSpace: "nowrap" }}
    >
      {children} →
    </Box>
  );
};

export const EmptyState = ({ children = "Aucune donnée", icon }) => (
  <Stack alignItems="center" spacing={1} sx={{ py: 4, color: "text.secondary" }}>
    {icon}
    <Typography variant="body2" color="text.secondary">{children}</Typography>
  </Stack>
);

const BookingLabel = () => {
  const b = useRecordContext();
  if (!b) return null;
  const who = fullName(b.firstNamePatient, b.lastNamePatient);
  return (
    <Box sx={{ lineHeight: 1.2 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main", whiteSpace: "nowrap" }}>
        {fmtDateTime(b?.slot?.startTime, "RDV")}
      </Typography>
      {(who || b.nameSpeciality) && (
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
          {[who, b.nameSpeciality].filter(Boolean).join(" · ")}
        </Typography>
      )}
    </Box>
  );
};

// Remplace une colonne « bookingId » par la date + le patient du RDV (lien vers la fiche).
export const BookingRefField = ({ source = "bookingId", emptyText = "—" }) => (
  <ReferenceField source={source} reference="bookings" link="show" emptyText={emptyText}>
    <BookingLabel />
  </ReferenceField>
);
